import * as d3 from 'd3'
import * as dateFns from 'date-fns'

import { Instrument, ObjectHash } from '../../types/shared'
import type PerfChart from '../charts/perf_chart'
import {
  DateRangeType,
  MAIN_MARKET_MINUTES_LENGTH,
  ScaleType,
  SpecificChartFunctionality,
  TIMEFRAME,
} from '../constants/common'
import Chart from '../models/chart'
import ChartEventElement from '../models/chart-event-element'
import ChartLayout from '../models/chart_layout'
import { IChartSettings, IIndicatorSettings, ISettings } from '../models/chart_settings/interfaces'
import { CHART_EVENT_BADGE_SIZE } from '../models/constants'
import Element from '../models/element'
import Pane, { ScaleAxis } from '../models/pane'
import Quote from '../models/quote'
import Utils from '../utils'
import utils, { convertLocalToNyTime, dateFromUnixTimestamp, isRedesignedPage } from '../utils'
import { getBarWidthWithMargin, getZoomFactorForBarsToDisplay } from '../utils/chart'
import {
  DATE_RANGE_PREFIX_DELMITER,
  getParsedDateRangeMetaData,
  parseCustomDateRangeUrlParam,
} from '../utils/chart-date-range-utils'
import { getVisibleBarToRenderIndex } from '../utils/draw_in_visible_area'
import { getIsSSr } from '../utils/helpers'

interface VisibleBarNumberProps {
  start: Date | null
  end: Date
  quote: Quote
  dateRange: string
}

interface ChartZoomAndLeftOffsetProps extends Omit<VisibleBarNumberProps, 'dateRange'> {
  chartVisibleWidth: number
  numOfBarsToRender?: number
  chartModel: Chart
}

interface ChartStartEndDatesProps {
  dateRange: string
  quote?: Quote
  anchorDate?: Date
}

export const getVisibleBarsNumber = ({ start, end, quote, dateRange }: VisibleBarNumberProps) => {
  const startInSec = start ? start.getTime() / 1000 : 0
  const endInSec = end.getTime() / 1000
  const isQuoteDateInRange = (quoteDate: number) => quoteDate >= startInSec && quoteDate <= endInSec
  const lastQuoteDateIndex = quote.barIndex[quote.barIndex.length - 1]
  let lastVisibleBarIndex = lastQuoteDateIndex
  let numOfVisibleBars = 0
  if (
    [DateRangeType.d1, DateRangeType.d5].includes(dateRange as DateRangeType) &&
    quote.instrument === Instrument.Stock
  ) {
    numOfVisibleBars = quote.drawMinutesPerDay ?? 0
  } else {
    let firstVisibleBarIndex = -1
    lastVisibleBarIndex = -1
    quote.date.forEach((quoteDate, index) => {
      if (isQuoteDateInRange(quoteDate)) {
        if (firstVisibleBarIndex === -1) {
          firstVisibleBarIndex = quote.barIndex[index]
        }
        lastVisibleBarIndex = quote.barIndex[index]
      }
    })
    numOfVisibleBars = lastVisibleBarIndex - firstVisibleBarIndex + 1

    if (firstVisibleBarIndex === -1 && lastVisibleBarIndex === -1) {
      return { numOfVisibleBars: 0, numOfBarsOffset: 0 }
    }
  }

  const numOfBarsOffset = lastQuoteDateIndex - lastVisibleBarIndex
  return { numOfVisibleBars, numOfBarsOffset: Number.isNaN(numOfBarsOffset) ? 0 : numOfBarsOffset }
}

export const getChartZoomAndLeftOffset = ({
  chartVisibleWidth,
  start,
  end,
  quote,
  chartModel,
  numOfBarsToRender,
}: ChartZoomAndLeftOffsetProps) => {
  const { numOfVisibleBars, numOfBarsOffset } = getVisibleBarsNumber({
    start,
    end,
    quote,
    dateRange: chartModel.dateRange,
  })
  if (numOfVisibleBars === 0) {
    return { zoomFactor: chartModel.zoomFactor, leftOffset: chartModel.leftOffset }
  }

  const chartLayout = chartModel.chart_layout()
  const zoomFactor = getZoomFactorForBarsToDisplay({
    chartLayout,
    chartVisibleWidth,
    numOfVisibleBars: numOfBarsToRender ?? numOfVisibleBars,
  })
  const barWidth = getBarWidthWithMargin({ chartLayout, zoomFactor })
  const leftOffset =
    getLeftOffset({
      quote,
      chartModel,
      config: {},
      barWidth,
      numberOfBarsToDraw: numOfBarsToRender,
      roundingMethod: zoomFactor === 1 ? 'round' : 'floor',
    }) +
    numOfBarsOffset * barWidth
  return { zoomFactor, leftOffset }
}

export const getChartStartEndDates = ({ dateRange, quote, anchorDate = new Date() }: ChartStartEndDatesProps) => {
  const daysInQuote = quote?.getDaysInQuote() ?? []
  const lastDate =
    quote && quote?.date.length > 0 ? dateFromUnixTimestamp(quote.date[quote.date.length - 1]) : anchorDate

  if (dateRange.includes(DATE_RANGE_PREFIX_DELMITER)) {
    const range = parseCustomDateRangeUrlParam({ dateRange, anchorDate: lastDate })
    const end = convertLocalToNyTime(
      range.end && range.end.getTime() <= lastDate.getTime() ? range.end : lastDate,
      false
    )
    return {
      start: range.start && convertLocalToNyTime(range.start, false),
      end,
    }
  }

  let start
  // No condition is needed here, but this would be dateRangePrefix === DateRangePrefixEnum.Enum
  switch (dateRange) {
    case DateRangeType.d1:
      lastDate.setHours(23, 59, 59, 999)
      start = daysInQuote.length === 0 ? dateFns.startOfDay(lastDate) : daysInQuote[daysInQuote.length - 1]
      break
    case DateRangeType.d5:
      lastDate.setHours(23, 59, 59, 999)
      const startDateIndex = daysInQuote.length === 0 ? -1 : Math.max(daysInQuote.length - 5, 0)
      // subDays 4 is correct because we're taking the whole day (dateFns.startOfDay)
      start = startDateIndex === -1 ? dateFns.startOfDay(dateFns.subDays(lastDate, 4)) : daysInQuote[startDateIndex]
      break
    case DateRangeType.m1:
      start = dateFns.subMonths(lastDate, 1)
      break
    case DateRangeType.m3:
      start = dateFns.subMonths(lastDate, 3)
      break
    case DateRangeType.m6:
      start = dateFns.subMonths(lastDate, 6)
      break
    case DateRangeType.ytd:
      start = dateFns.startOfYear(lastDate)
      break
    case DateRangeType.y1:
      start = dateFns.subYears(lastDate, 1)
      break
    case DateRangeType.y2:
      start = dateFns.subYears(lastDate, 2)
      break
    case DateRangeType.y5:
      start = dateFns.subYears(lastDate, 5)
      break
    case DateRangeType.max:
    default:
      start = null
      break
  }

  return { start: start && convertLocalToNyTime(start, false), end: convertLocalToNyTime(lastDate, false) }
}

interface GetLeftOffsetProps {
  quote: Quote
  chartModel: Chart
  config?: { stretch?: boolean }
  barWidth?: number
  barIndex?: number
  numberOfBarsToDraw?: number
  roundingMethod?: 'floor' | 'round'
  shouldAlignDaysToLeftApplied?: boolean
}

export const getMinMaxLeftOffset = ({
  newLeftOffset,
  chartModel,
  barWidth = getBarWidthWithMargin({ zoomFactor: chartModel.zoomFactor, chartLayout: chartModel.chart_layout() }),
  barIndex,
  roundingMethod,
}: {
  newLeftOffset: number
  chartModel: Chart
  barWidth?: number
  barIndex: number
  roundingMethod?: GetLeftOffsetProps['roundingMethod']
}) => {
  const { ChartSettings } = chartModel.getChartLayoutSettings()
  const width = chartModel.width - ChartSettings.left.width - ChartSettings.right.width
  const numOfBarsInView = Math.floor(width / barWidth)
  const fx = (index: number) => index * barWidth

  // Keep Math.round in sync with calculation of leftOffset in getLeftOffset
  const leftOffset = Utils.min(width - barWidth / 2, Utils.max(newLeftOffset, width - fx(barIndex + numOfBarsInView)))

  if (roundingMethod === 'floor') {
    return Math.floor(leftOffset)
  }
  // If barWidth is less than ~2.5 we render just its wick, and to avoid blurred lines we have to round the left offset which is then used in translate function
  if (roundingMethod === 'round' || chartModel.zoomFactor === 1 || barWidth < 2.5) {
    return Math.round(leftOffset)
  }
  return leftOffset
}

export function getIntradayChartConfig({
  timeframeMinutes,
  barWidthWithMargin,
  premarketMinutesLength,
  aftermarketMinutesLength,
  maxWidth,
  canCropChart,
}: {
  premarketMinutesLength: number
  aftermarketMinutesLength: number
  timeframeMinutes: number
  barWidthWithMargin: number
  maxWidth: number
  canCropChart: boolean
}) {
  const mainMarketBars = MAIN_MARKET_MINUTES_LENGTH / timeframeMinutes
  const marketMinutesLength = MAIN_MARKET_MINUTES_LENGTH + premarketMinutesLength + aftermarketMinutesLength
  const fullDayChartBars = marketMinutesLength / timeframeMinutes
  const maxBarsInView = Math.floor(maxWidth / barWidthWithMargin)

  const viewsNeededToRenderFullDay = maxBarsInView && Math.ceil(fullDayChartBars / maxBarsInView)

  /*
   * If whole day (premarket + main market + aftermarket) is larger than chart maxWidth
   * which we can derive from viewsNeededToRenderFullDay > 1
   * we need to calculate offset breakpoint (when & how much should the chart be scrolled)
   *
   * offsetAfterBars is array of breakpoints
   * where scrollNumOfBars tells us how much we should scroll the chart
   * and scrollAfterBars when we should apply the scroll (after we've rendered the amount of bars)
   *
   * The reason we have 2 separate variables, is to be able to scroll only portion of the chart width after we reach the end
   * because we want to render whole main market, so if we render premarket and first 3 hours of the main market,
   * we want to be able to scroll only so much we render the whole main market and we want to do it when we reach the end of after market
   * */
  const offsetAfterBars: Array<{ scrollAfterBars: number; scrollNumOfBars: number }> = []
  if (viewsNeededToRenderFullDay > 1) {
    let barsRendered = 0
    const premarketBars = premarketMinutesLength / timeframeMinutes
    while (barsRendered + maxBarsInView < fullDayChartBars) {
      const lastVal = offsetAfterBars.last()?.scrollAfterBars ?? 0
      let scrollAfterBars = barsRendered + maxBarsInView
      let newOffset = barsRendered + maxBarsInView

      /*
       * This is a check for the first breakpoint
       * We set the breakpoint (scrollAfterBars) to the start of main market (end of premarket)
       * and we set the scroll position to the beginning of the main market
       * */
      if (lastVal < premarketBars && newOffset > premarketBars) {
        newOffset = premarketBars
        scrollAfterBars = premarketBars
        const extraBars = maxBarsInView - mainMarketBars
        /*
         * If the chart is wider than main market
         * instead of rendering large empty aftermarket
         * we align the chart to the right so the main market is fully rendered and extra spaces is used for premarket
         * */
        if (extraBars > 0) {
          newOffset -= Math.min(extraBars, newOffset)
        }
      }

      /*
       * This cover the case for the last breakpoint,
       * we need to scroll only so much so we have the last bar at the very end of the chart
       * */
      if (newOffset + maxBarsInView > fullDayChartBars) {
        newOffset = fullDayChartBars - maxBarsInView
      }

      barsRendered += newOffset
      offsetAfterBars.push({
        scrollAfterBars,
        scrollNumOfBars: newOffset,
      })
    }
  }

  const doesMainMarketFitIntoMaxWidth = mainMarketBars * barWidthWithMargin <= maxWidth
  const fullChartWidth = fullDayChartBars * barWidthWithMargin
  const shouldCropChart = !!(doesMainMarketFitIntoMaxWidth && canCropChart && offsetAfterBars.length > 0)
  return {
    shouldCropChart,
    offsetInPx: shouldCropChart ? fullChartWidth : 0,
    offsetAfterBars,
    chartWidth: shouldCropChart ? maxBarsInView * barWidthWithMargin : Math.min(fullChartWidth, maxWidth),
  }
}

export const getLeftOffset = ({
  quote,
  chartModel,
  config,
  barWidth = getBarWidthWithMargin({ zoomFactor: chartModel.zoomFactor, chartLayout: chartModel.chart_layout() }),
  barIndex = getBarIndex(quote),
  numberOfBarsToDraw,
  roundingMethod,
  shouldAlignDaysToLeftApplied = true,
}: GetLeftOffsetProps) => {
  const { ChartSettings } = chartModel.getChartLayoutSettings()
  const width = chartModel.width - ChartSettings.left.width - ChartSettings.right.width
  const specificChartFunctionality = chartModel.chart_layout().specificChartFunctionality
  if (config?.stretch) {
    return 0
  }
  if (getIsSmallIndexChart(specificChartFunctionality)) {
    return barWidth
  }
  const fx = (index: number) => index * barWidth
  let leftOffset = width - fx(barIndex) - Math.round(barWidth / 2)
  // Keep Math.round in sync with return value in getMinMaxLeftOffset
  leftOffset = Utils.min(barWidth / 2, Utils.max(leftOffset, width - fx(barIndex + 45)))
  if (roundingMethod === 'floor') {
    leftOffset = Math.floor(leftOffset)
  } else if (roundingMethod === 'round' || chartModel.zoomFactor === 1) {
    leftOffset = Math.round(leftOffset)
  }
  if (
    shouldAlignDaysToLeftApplied &&
    shouldAlignDaysToLeft({
      quote,
      numberOfBarsToDraw,
      specificChartFunctionality,
      dateRange: chartModel.dateRange,
      zoomFactor: chartModel.zoomFactor,
    })
  ) {
    let drawDaysInOneChart = width / (quote.getBarsInDay() * barWidth)

    if (chartModel.dateRange === DateRangeType.d5) {
      drawDaysInOneChart = 5
    } else if ([TIMEFRAME.i1, TIMEFRAME.i2, TIMEFRAME.i3, TIMEFRAME.i5].includes(quote.timeframe)) {
      drawDaysInOneChart = 1
    }

    if (numberOfBarsToDraw) {
      drawDaysInOneChart = Math.ceil(numberOfBarsToDraw / quote.getBarsInDay())
    }
    const { offsetAfterBars, shouldCropChart } = getIntradayChartConfig({
      premarketMinutesLength: quote.premarketLengthMinutes,
      aftermarketMinutesLength: quote.aftermarketLengthMinutes,
      barWidthWithMargin: barWidth,
      timeframeMinutes: quote.interval,
      maxWidth: width,
      canCropChart: !chartModel.dateRange,
    })
    const drawDaysWidth = drawDaysInOneChart * quote.getBarsInDay() * barWidth
    const numOfDaysInQuoteData = Object.keys(quote.getDayToOffset()).length

    // +1 is a buffer as it often happens drawDaysWidth is wider than width by tiny amount (< 1/10000 of px)
    if ((shouldCropChart || drawDaysWidth <= width + 1) && numOfDaysInQuoteData >= drawDaysInOneChart) {
      const lastDate = quote.date.last() ?? quote.lastTime
      let lastQuoteDate = lastDate ? utils.dateFromUnixTimestamp(lastDate) : null

      // See Quote.getDayToOffset for reference
      // On node charts if day of last data is newer than day of last bar, we render day of last data
      // eg. preMarket is set to 7AM or disabled, but preMarket started on 4AM, last data will contain
      // new day, but last bar data will be from previous day, in such case we start rendering new day
      // which appears empty, but contain latest OHLC
      if (getIsSSr() && quote.lastTime && quote.lastTime > lastDate) {
        lastQuoteDate = utils.dateFromUnixTimestamp(quote.lastTime)
      }

      // Align start of day to left if we have enough space to show all bars in day
      const barsToOffset = (numOfDaysInQuoteData - drawDaysInOneChart) * quote.getBarsInDay()
      const barsRendered =
        ((lastQuoteDate?.getHours() ?? 0) * 60 + (lastQuoteDate?.getMinutes() ?? 0) - quote.marketStartMinutes) /
        quote.interval
      const offsetBars =
        offsetAfterBars.findLast(({ scrollAfterBars }) => scrollAfterBars <= barsRendered)?.scrollNumOfBars ?? 0
      const offsetChartInPx = offsetBars * barWidth
      // Math.max(1, ~~num) is to mimic implementation from getHalfBarWidth(),
      // we never render bar with width < 1 but at the same time when we have and odd
      // width like 5, wa want to floor because of pixel perfect rendering
      leftOffset = Math.round(-fx(barsToOffset) + Math.max(1, ~~(barWidth / 2))) - offsetChartInPx
    }
  }
  // getMinMaxLeftOffset move left offset to keep at least one bar in view what isn't desired for offscreen charts
  if (specificChartFunctionality === SpecificChartFunctionality.offScreen) {
    return leftOffset
  }
  return getMinMaxLeftOffset({
    newLeftOffset: leftOffset,
    chartModel,
    barWidth,
    barIndex,
    roundingMethod,
  })
}

export const getBarIndex = (quote: Quote) => quote?.getBarIndex(quote.open?.length ? quote.open.length - 1 : 0) || 0

export const getIsSmallIndexChart = (specificChartFunctionality: SpecificChartFunctionality) =>
  specificChartFunctionality === SpecificChartFunctionality.smallIndex

interface ShouldAlignDaysToLeftProps {
  quote: Quote
  numberOfBarsToDraw?: number
  specificChartFunctionality: SpecificChartFunctionality
  dateRange: DateRangeType | null
  zoomFactor: number
}

/**
 * Evaluate if days should be aligned to the left
 * @param {object} object.quote - Quote model
 * @param {string} object.specificChartFunctionality - SpecificChartFunctionality enum
 * @returns {boolean} boolean
 * */
export const shouldAlignDaysToLeft = ({
  quote,
  dateRange,
  zoomFactor,
  numberOfBarsToDraw,
  specificChartFunctionality,
}: ShouldAlignDaysToLeftProps) => {
  const isStockIntraday = quote.isIntraday && quote.instrument === Instrument.Stock
  const isIntradayDateRange = !!dateRange && [DateRangeType.d1, DateRangeType.d5].includes(dateRange)
  const isOffScreen = specificChartFunctionality === SpecificChartFunctionality.offScreen
  const isOffScreenWithNoDateRange = isOffScreen && !dateRange
  const isOffScreenIntradayDateRange = isOffScreen && isIntradayDateRange
  const isOffScreenWithFixedBars = isOffScreen && numberOfBarsToDraw

  const isQuoteOrQuoteFinancials = [
    SpecificChartFunctionality.quotePage,
    SpecificChartFunctionality.quoteFinancials,
  ].includes(specificChartFunctionality)
  const isQuotePageWithNoDateRange = isQuoteOrQuoteFinancials && !dateRange
  const isQuotePageIntradayDateRange = isQuoteOrQuoteFinancials && isIntradayDateRange
  const isQuotePageWithFixedBars = isQuoteOrQuoteFinancials && numberOfBarsToDraw

  const isChartPage = specificChartFunctionality === SpecificChartFunctionality.chartPage
  const isChartsInradayDateRange = isChartPage && isIntradayDateRange
  const isChartPageWithFixedBars = isChartPage && numberOfBarsToDraw
  return (
    // align only if the chart is not zoomed or date range is selected (we do the alignemnt for D1 and D5 date ranges)
    (zoomFactor === 1 || dateRange) &&
    isStockIntraday &&
    (isOffScreenWithNoDateRange ||
      isOffScreenIntradayDateRange ||
      isOffScreenWithFixedBars ||
      isQuotePageWithNoDateRange ||
      isQuotePageIntradayDateRange ||
      isQuotePageWithFixedBars ||
      isChartsInradayDateRange ||
      isChartPageWithFixedBars)
  )
}

interface ClipProps {
  canvasCtx: RenderingContext2DType
  width: number
  height: number
  Settings: ISettings['ChartSettings'] | ISettings['IndicatorSettings']
}
// pane canvasCtx clip
export const clip = ({ canvasCtx, width, height, Settings }: ClipProps) => {
  const contentWidth = width - Settings.left.width - Settings.right.width
  const contentHeight = height - Settings.top.height - Settings.bottom.height
  canvasCtx.beginPath()
  canvasCtx.rect(Settings.left.width, Settings.top.height, contentWidth, contentHeight)
  canvasCtx.clip()
}

export const getSettings = (paneModel: Pane): IIndicatorSettings | IChartSettings => {
  const { ChartSettings, IndicatorSettings } = paneModel.chart().getChartLayoutSettings()
  for (const element of paneModel.elements().all()) {
    if (element.isIndicator()) {
      return IndicatorSettings
    }
  }
  return ChartSettings
}

interface RecountScaleProps {
  chartModel: Chart
  paneModel: Pane
}

type ScaledFn = Partial<ScaleAxis> & (d3.ScaleLinear<number, number> | d3.ScaleSymLog<number, number>)

const getShouldOffsetChartDomain = (elementModel: Element | ChartEventElement) => {
  const paneModel = elementModel.pane()
  const chart = paneModel.chart()
  const quote = chart.quote()

  const { ChartSettings } = chart.getChartLayoutSettings()

  const visibleBarToRenderIndexProps = {
    leftOffset: chart.leftOffset,
    paneModel,
    quote,
  }
  const firstBarIndex = getVisibleBarToRenderIndex(visibleBarToRenderIndexProps)
  const lastBarIndex = getVisibleBarToRenderIndex({
    ...visibleBarToRenderIndexProps,
    chartWidth: chart.width - ChartSettings.left.width - ChartSettings.right.width,
  })

  if ([TIMEFRAME.w, TIMEFRAME.m].includes(quote.timeframe)) {
    return false
  }

  return quote.chartEvents.some(
    ({ dateTimestamp }) =>
      quote.date[firstBarIndex.dataIndex] <= dateTimestamp && quote.date[lastBarIndex.dataIndex] >= dateTimestamp
  )
}

export const recountScale = ({ chartModel, paneModel }: RecountScaleProps) => {
  const { ChartSettings, IndicatorSettings, MarketSentimentSettings } = chartModel.getChartLayoutSettings()
  const chartLayout = chartModel.chart_layout()
  const barWidthWithMargin = getBarWidthWithMargin({ chartLayout, zoomFactor: chartModel.zoomFactor })
  for (const elementModel of [...paneModel.getAllElements(), ...paneModel.getAllChartEvents(false)]) {
    const el = elementModel.instance
    const isChart = elementModel.isChart()
    if (isChart || elementModel.isIndicator()) {
      let fx: ScaledFn, Settings: ISettings['MarketSentimentSettings' | 'ChartSettings' | 'IndicatorSettings'], width
      const scaleType = isChart ? chartModel.scale : ScaleType.Linear
      if (isChart) {
        Settings = chartModel.instrument === Instrument.MarketSentiment ? MarketSentimentSettings : ChartSettings
      } else {
        Settings = IndicatorSettings as ISettings['IndicatorSettings']
      }
      const contentHeight = paneModel.height - Settings.top.height - Settings.bottom.height

      if (chartModel.stretch) {
        width = chartModel.width - Settings.left.width - Settings.right.width
        const chartElement = chartModel.getChartElement()
        // PerfChart can be only imported as type because of circular dependency, that's why it's not possible to do instanceof check
        const isPerfChart = !!chartElement?.instance && 'getCompleteChartNumOfBars' in chartElement?.instance
        const perfChartInstance = isPerfChart ? (chartElement.instance as PerfChart) : null
        const numOfBars = perfChartInstance?.getCompleteChartNumOfBars() ?? chartModel.quote().close.length
        fx = d3
          .scaleLinear()
          .range([0, width])
          .domain([0, Math.max(0, numOfBars - 1)]) as ScaledFn
      } else {
        fx = ((index: number) => index * barWidthWithMargin) as ScaledFn
        fx.invert = (x: number) => x / barWidthWithMargin
        fx.width = function (w) {
          return this.invert(w) - this.invert(0)
        }
      }
      paneModel.updateAttribute('scale', { x: fx }) // some elements need fx to count getMinMax

      const { min, max } = paneModel.scaleRange || el.getMinMax!()
      let fy

      const handleDomainOffset = (currentScale: ScaledFn, scaleCreator: any) => {
        if (isChart && getShouldOffsetChartDomain(elementModel)) {
          const protectedHeightPx = contentHeight - CHART_EVENT_BADGE_SIZE
          const minPx = currentScale(min)
          const domain = currentScale.domain()
          if (minPx > protectedHeightPx) {
            const tempScale = scaleCreator().range([0, protectedHeightPx]).domain([max, min])
            const extendedMin = tempScale.invert(contentHeight)
            currentScale.domain([domain[0], extendedMin])
          }
        } else if (elementModel.isFinancialIndicator()) {
          // The reason for this casting is that we can't import FinancialIndicator because it would cause cricular dependency
          const dotWidth = (elementModel.instance as unknown as { getDotWidth: () => number }).getDotWidth()
          if (dotWidth > 0) {
            const protectedHeightPx = contentHeight - dotWidth
            const minPx = currentScale(min)
            const maxPx = currentScale(max)
            const isOverBottom = minPx > protectedHeightPx
            const isOverTop = maxPx < dotWidth
            if (isOverBottom || isOverTop) {
              const tempScale = scaleCreator()
                .range([dotWidth, protectedHeightPx - dotWidth])
                .domain([max, min])
              const extendedMin = tempScale.invert(contentHeight)
              const extendedMax = tempScale.invert(0)
              currentScale.domain([extendedMax, extendedMin])
            }
          }
        }
      }

      switch (scaleType) {
        case ScaleType.Logarithmic:
          fy = d3.scaleSymlog().range([0, contentHeight]).domain([max, min]) as ScaledFn
          handleDomainOffset(fy, d3.scaleSymlog)
          break
        default:
          fy = d3.scaleLinear().range([0, contentHeight]).domain([max, min]) as ScaledFn
          if (!paneModel.scaleRange) {
            fy = fy.nice(10)
            handleDomainOffset(fy, d3.scaleLinear)
          }
          break
      }
      fy.height = function (h: number) {
        return this.invert(h) - this.invert(0)
      }
      paneModel.updateAttribute('scale', {
        x: fx,
        y: fy,
      })

      if (isChart) {
        el.setupAxis!(fx as ScaleAxis)
      }
      break
    }
  }
}

// general, only when blurry ends won't be visible or didn't matter
export const getOffsetFromLineWidth = (lineWidth: number) => (lineWidth % 2 === 0 ? 0 : 0.5)

interface IXYOffsetFromLine {
  lineWidth: number
  x1: number
  y1: number
  x2: number
  y2: number
}

// return x,y offsets for pixel perfect line rendering
export const getXYOffsetFromLine = ({ lineWidth, x1, y1, x2, y2 }: IXYOffsetFromLine) => {
  // don't add offset is lineWidth is even
  if (lineWidth % 2 === 0) {
    return { x: 0, y: 0 }
  }
  if (y1 === y2) {
    return { x: 0, y: 0.5 } // horizontal line
  } else if (x1 === x2) {
    return { x: 0.5, y: 0 } // vertical line
  } else {
    return { x: 0.5, y: 0.5 } // diagonal line
  }
}

export const getRoundedObject = <T extends ObjectHash>(obj: T): T => {
  const rounded = Object.entries<number>(obj)
  for (let i = 0; i < rounded.length; i++) {
    rounded[i][1] = Math.round(rounded[i][1])
  }
  return Object.fromEntries(rounded) as T
}

interface ITranslate {
  context: CanvasRenderingContext2D
  xOffset: number
  yOffset: number
}

interface ITranslateObj {
  do: () => void
  undo: () => void
}

export const getTranslate = ({ context, xOffset, yOffset }: ITranslate): ITranslateObj => ({
  do: () => context.translate(xOffset, yOffset),
  undo: () => context.translate(xOffset * -1, yOffset * -1),
})

interface IPercentageScaleValue {
  number: number
  base: number
}

export const getPercentageFromValue = ({ number, base }: IPercentageScaleValue) => ((number - base) / base) * 100

export const getValueFromPercentage = ({ number, base }: IPercentageScaleValue) => (number / 100) * base + base

interface UpdateZoomAndLeftOffsetByDateRangeProps {
  chartModel: Chart
  quote: Quote
}

export function updateZoomAndLeftOffsetByDateRange({ chartModel, quote }: UpdateZoomAndLeftOffsetByDateRangeProps) {
  const { ChartSettings } = chartModel.getChartLayoutSettings()
  const chartVisibleWidth = chartModel.width - (ChartSettings.left.width + ChartSettings.right.width)

  const { start, end, numOfBarsToRender } = getParsedDateRangeMetaData({
    dateRange: chartModel.dateRange,
    quote,
  })

  const { zoomFactor, leftOffset } = getChartZoomAndLeftOffset({
    chartVisibleWidth,
    start,
    end,
    quote,
    numOfBarsToRender,
    chartModel: chartModel,
  })

  if (chartModel.zoomFactor !== zoomFactor || chartModel.leftOffset !== leftOffset) {
    chartModel.updateAttributes({ zoomFactor, leftOffset })
  }
}

export function renderPane({
  chartLayoutModel,
  chartModel,
  paneModel,
  canvasCtx,
  shouldRenderCross = false,
}: {
  chartLayoutModel: ChartLayout
  chartModel: Chart
  paneModel: Pane
  canvasCtx: CanvasRenderingContext2D
  shouldRenderCross?: boolean
}) {
  if (paneModel.chart().instrument === Instrument.MarketSentiment) {
    canvasCtx.clearRect(0, 0, chartModel.width, paneModel.height)
  } else {
    canvasCtx.set('fillStyle', chartModel.getChartLayoutSettings().ChartSettings.general.Colors.canvasFill)
    canvasCtx.fillRect(0, 0, chartModel.width, paneModel.height)
  }
  const elements = paneModel.getAllElements()
  for (const { instance: elementInstance } of elements) {
    if (typeof elementInstance.renderGrid === 'function') {
      canvasCtx.save()
      elementInstance.renderGrid(canvasCtx)
      canvasCtx.restore()
    }
  }

  const chartEvents = paneModel.getAllChartEvents()
  const Settings = getSettings(paneModel)
  for (const { instance: elementInstance } of [...elements, ...chartEvents]) {
    const isDrawing = elementInstance.getIsDrawing()
    const isChartEvent = elementInstance.getIsChartEvent()

    if (isDrawing && (chartLayoutModel.isHideDrawingsActive || !elementInstance.getIsVisible())) {
      continue
    } else if ((isDrawing || isChartEvent) && !elementInstance.getIsInChartView(chartModel)) {
      continue
    }

    canvasCtx.save()
    if (elementInstance.renderContent != null) {
      canvasCtx.save()
      clip({
        canvasCtx,
        width: chartModel.width,
        height: paneModel.height,
        Settings,
      })
      canvasCtx.translate(paneModel.chart().leftOffset + Settings.left.width, Settings.top.height)
      elementInstance.renderContent(canvasCtx)
      canvasCtx.restore()
      if (typeof elementInstance.renderLabels === 'function') {
        elementInstance.renderLabels(canvasCtx)
      }
    } else {
      elementInstance.render(canvasCtx)
    }
    if (
      shouldRenderCross &&
      (chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.chartPage ||
        isRedesignedPage(chartLayoutModel)) &&
      elementInstance.renderCross != null
    ) {
      elementInstance.renderCross(canvasCtx)
    } else if (
      shouldRenderCross &&
      chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.offScreen &&
      elementInstance.renderCrossText != null
    ) {
      elementInstance.renderCrossText(canvasCtx, Number.NaN)
    }
    canvasCtx.restore()
  }
}
